import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, GraduationCap, CalendarClock, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const inert = (e) => e.preventDefault();

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Brand bar motif — derived from the WFII logo (vertical bars + teal square) */
const LogoBars = ({ dark = false, className = "" }) => {
  const bar = dark ? "bg-white/15" : "bg-[hsl(var(--primary))]/15";
  return (
    <div className={`flex items-end gap-1 ${className}`} aria-hidden="true">
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 28 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 15 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 22 }} />
      <span className="w-1 rounded-[2px] bg-[hsl(var(--accent))]/50" style={{ height: 10 }} />
    </div>
  );
};

/* ================= Block 1 — Program hero ================= */

const ProgramHero = () => (
  <section data-testid="accounting-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="grid gap-[35px] sm:gap-[43px] lg:grid-cols-[600px_1fr] lg:items-center lg:gap-10">
        {/* Left — editorial text */}
        <div className="mx-auto max-w-[62rem] text-center lg:mx-0 lg:max-w-none lg:self-start lg:text-left">
          <div className="flex items-center justify-center gap-3 lg:justify-start" aria-hidden="true">
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 lg:hidden" />
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
              WFII Program
            </span>
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))] lg:hidden" />
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 lg:hidden" />
          </div>
          <h1 className="font-serif-editorial mx-auto mt-8 text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:mx-0 lg:text-[3.6rem]">
            Digital Asset Accounting
          </h1>
          <div
            className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:gap-6 lg:flex-nowrap lg:justify-start lg:whitespace-nowrap"
            data-testid="accounting-hero-enrollment-banner"
          >
            <span className="flex items-center gap-2.5 text-sm font-medium text-foreground lg:text-[13px]">
              <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--accent))] opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[hsl(var(--accent))]" />
              </span>
              Next program: January 2027
            </span>
            <span className="hidden h-5 w-px bg-[hsl(var(--accent))]/25 sm:block" aria-hidden="true" />
            <span className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--accent))] lg:text-[13px]">
              <CalendarClock className="h-4 w-4 shrink-0" strokeWidth={1.9} />
              Applications are open until December 23, 2026.
            </span>
          </div>
          <p className="mx-auto mt-8 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0 lg:max-w-none">
            Learn how to approach digital asset records, reconciliation,
            reporting,
            <br className="hidden lg:block" aria-hidden="true" /> and{" "}
            <br className="lg:hidden" />
            accounting workflows.
          </p>
          <p className="mx-auto mt-4 max-w-[74ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0 lg:max-w-none">
            A business-focused program for professionals who need to understand
            how digital assets may appear in business operations and what
            finance teams should know before recording, explaining, or
            reconciling{" "}
            <br className="hidden lg:block" aria-hidden="true" />
            related activity.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 lg:justify-start">
            <Button
              asChild
              className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
              data-testid="accounting-hero-enroll-button"
            >
              <Link to="/enrollment-request" state={{ program: "digital-asset-accounting" }}>
                Apply Now
              </Link>
            </Button>
            <Link
              to="/enrollment-request" state={{ type: "membership" }}
              className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
              data-testid="accounting-hero-become-member-link"
            >
              Become a Member
              <ArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                strokeWidth={1.75}
              />
            </Link>
          </div>
        </div>

        {/* Right — program image */}
        <div className="overflow-hidden rounded-2xl shadow-[0_40px_80px_-40px_hsl(218_51%_15%/0.4)] ring-1 ring-[hsl(var(--primary))]/10 lg:ml-auto lg:w-[85%]">
          <img
            src="/program-accounting-3.jpg"
            alt="Modern financial district office tower at night"
            className="wfii-photo aspect-[16/10] w-full object-cover sm:aspect-[21/9] lg:aspect-[3/2]"
            loading="eager"
            data-testid="accounting-hero-image"
          />
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 2 — Who this program is for ================= */

const WhoItsFor = () => (
  <section data-testid="accounting-audience-section" className="!mt-[40px] lg:!mt-[120px]">
    <Container className="">
      <div className="grid gap-10 lg:grid-cols-[1fr_496px] lg:items-end lg:gap-x-[28px] lg:gap-y-[14px]">
        <h2 className="mt-6 text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:col-span-2 lg:mt-[-6px] lg:text-center lg:text-5xl">
          Who This Program Is For
        </h2>
        <div>
          <p className="-mt-[14px] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mt-10">
            This program is for professionals who are starting to see digital
            assets, stablecoins, wallets, or exchange activity appear in
            accounting and finance conversations.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            It is especially relevant for those working with bookkeeping,
            reconciliation, reporting, client records, finance operations, or
            internal documentation.
          </p>
        </div>
        <div
          className="rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] sm:p-8 lg:pb-[22px] lg:pt-[22px] lg:px-[26px]"
          data-testid="accounting-designed-for-card"
        >
          <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Designed for
          </span>
          <p className="mt-3 text-base leading-relaxed text-foreground/85">
            Accountants, bookkeepers, auditors, controllers, finance teams,
            and professionals supporting business clients.
          </p>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 3 — Program structure ================= */

const MODULES = [
  {
    n: "01",
    title: "Digital Asset Activity in Business",
    body: "Understand how digital assets and stablecoins may appear in business operations, payments, balances, and financial workflows.",
  },
  {
    n: "02",
    title: "Records and Documentation",
    body: "Learn what information finance teams should collect, including transaction history, wallet details, confirmations, exchange rates, and supporting records.",
  },
  {
    n: "03",
    title: "Reconciliation Workflows",
    body: "Understand how to approach digital asset activity from a reconciliation and bookkeeping workflow perspective.",
  },
  {
    n: "04",
    title: "Reporting Considerations",
    body: "Review common reporting, documentation, and communication challenges related to digital asset activity.",
  },
  {
    n: "05",
    title: "Client & Team Communication",
    body: "Learn how to discuss digital asset records with clients, auditors, finance teams, and other professional stakeholders.",
  },
  {
    n: "06",
    title: "Audit Readiness for Preparers",
    body: "Explore what documentation, reconciliation, and supporting evidence an auditor will request, and how to prepare digital asset records before review.",
  },
];

const INCLUDES = [
  "Online video lessons",
  "Practical bookkeeping examples",
  "Templates and checklists",
  "Final knowledge assessment",
  "WFII Certificate of Completion",
];

const ProgramStructure = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  return (
  <section data-testid="accounting-structure-section">
    <Container className="">
      <div className="grid gap-10 lg:grid-cols-[440px_1fr] lg:gap-16">
        {/* Left — heading, meta, includes (sticky while cards scroll) */}
        <div>
          <div className="lg:sticky lg:top-24">
            <span className="block h-[3px] w-12 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <h2 className="mt-6 text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
              Program Structure{" "}
              <br className="lg:hidden" />
              and Format
            </h2>
            <p className="mt-6 text-lg font-medium text-foreground">
              6 modules
              <span className="mx-3 text-muted-foreground/50">·</span>
              12 learning hours
            </p>
            <div
              className="relative mt-8 overflow-hidden rounded-2xl border border-[rgba(20,35,55,0.08)]"
              data-testid="accounting-preview-video"
            >
              <video
                ref={videoRef}
                className="program-preview-video aspect-video w-full bg-black object-cover"
                controls
                preload="metadata"
                poster="/stablecoins-video-thumbnail.webp"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              >
                <source
                  src="/accounting-program-video.mp4"
                  type="video/mp4"
                />
              </video>
              {!isPlaying && (
                <button
                  type="button"
                  onClick={() => videoRef.current?.play()}
                  aria-label="Play video"
                  className="group absolute inset-0 flex items-center justify-center bg-transparent"
                  data-testid="accounting-preview-video-play-button"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-[0_10px_24px_-8px_hsl(0_0%_0%/0.4)] transition-transform group-hover:scale-105 sm:h-20 sm:w-20">
                    <Play
                      className="h-6 w-6 text-primary sm:h-7 sm:w-7"
                      fill="currentColor"
                      strokeWidth={0}
                    />
                  </span>
                </button>
              )}
            </div>
            <ul className="mt-8 space-y-2.5" data-testid="accounting-includes-list">
              {INCLUDES.map((item) => (
                <li key={item} className="flex items-center gap-2.5 whitespace-nowrap">
                  <Check
                    className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--wfii-teal))]"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  <span className="text-sm text-muted-foreground sm:text-base">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right — module rows */}
        <div className="space-y-4" data-testid="accounting-modules-list">
          {MODULES.map((m) => (
            <div
              key={m.n}
              className="flex items-center gap-[17px] rounded-2xl bg-card/80 p-6 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-7 lg:gap-6"
              data-testid={`accounting-module-${m.n}`}
            >
              <span className="font-serif-editorial text-2xl font-semibold tracking-[-0.02em] text-[hsl(var(--wfii-teal))] sm:text-3xl">
                {m.n}
              </span>
              <div className="hidden h-10 w-px bg-[hsl(var(--primary))]/10 lg:block" aria-hidden="true" />
              <div>
                <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Module {parseInt(m.n, 10)}
                </span>
                <h3 className="mt-1 text-lg font-semibold tracking-[-0.01em] text-foreground sm:text-xl">
                  {m.title}
                </h3>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                  {m.body}
                </p>
              </div>
            </div>
          ))}
          <div
            className="flex items-center gap-[17px] rounded-2xl bg-card/80 p-6 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-7 lg:gap-6"
            data-testid="accounting-module-outcome"
          >
            <GraduationCap
              className="h-7 w-7 shrink-0 text-[hsl(var(--wfii-teal))] sm:h-8 sm:w-8"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <div className="hidden h-10 w-px bg-[hsl(var(--primary))]/10 lg:block" aria-hidden="true" />
            <div>
              <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Outcome
              </span>
              <h3 className="mt-1 text-lg font-semibold tracking-[-0.01em] text-foreground sm:text-xl">
                By the End of the Program
              </h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                Participants gain a clearer understanding of how to approach
                digital asset transactions from a bookkeeping and accounting
                workflow perspective — what information to request, how to
                identify documentation gaps, and when an issue may require
                professional accounting or tax advice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
  );
};

/* ================= Block 6 — Pricing ================= */

const Pricing = () => (
  <section data-testid="accounting-pricing-section">
    <Container className="">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Pricing
        </h2>
      </div>

      <div className="mx-auto mt-[36px] grid max-w-4xl gap-6 sm:mt-14 sm:grid-cols-2">
        {/* Program Access */}
        <div
          className="flex flex-col rounded-2xl bg-primary p-8 text-primary-foreground shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-10"
          data-testid="accounting-pricing-program-access-card"
        >
          <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
          <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em] text-primary-foreground">
            Program Access
          </h3>
          <p className="mt-5 flex items-baseline gap-2">
            <span className="text-4xl font-semibold tracking-[-0.02em] text-primary-foreground">
              CAD $39
            </span>
          </p>
          <p className="mt-4 text-base leading-relaxed text-primary-foreground/75">
            Get full access to this program and all included learning materials.
          </p>
          <div className="mt-8 flex flex-1 items-end">
            <Button
              asChild
              className="h-11 w-full bg-white px-6 text-sm font-medium text-primary shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.25)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
              data-testid="accounting-pricing-apply-now-button"
            >
              <Link to="/enrollment-request" state={{ type: "standard", program: "digital-asset-accounting" }}>
                Apply Now
              </Link>
            </Button>
          </div>
        </div>

        {/* Join the WFII Professional Community */}
        <div
          className="flex flex-col rounded-2xl bg-card/80 p-8 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-10"
          data-testid="accounting-pricing-community-card"
        >
          <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
          <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em] text-foreground">
            Join the WFII Professional Community
          </h3>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Access member opportunities, professional resources, updates, and
            future programs.
          </p>
          <div className="mt-8 flex flex-1 items-end">
            <Button
              asChild
              className="h-11 w-full bg-[hsl(var(--accent))] px-6 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
              data-testid="accounting-pricing-apply-membership-button"
            >
              <Link to="/enrollment-request" state={{ type: "membership", program: "digital-asset-accounting" }}>
                Apply for Membership
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 7 — Final CTA ================= */

const ProgramFinalCta = () => (
  <section data-testid="accounting-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-10 left-10 hidden sm:flex" />
        <LogoBars dark className="pointer-events-none absolute right-10 top-10 hidden rotate-180 sm:flex" />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:text-5xl">
            Build Confidence{" "}
            <br className="lg:hidden" />
            in Digital Asset{" "}
            <br className="hidden lg:block" aria-hidden="true" />
            Accounting Workflows
          </h2>
          <div className="mx-auto max-w-2xl">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              Develop the knowledge needed to understand records,
              reconciliation, and reporting considerations for digital asset
              activity in business finance.
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Page ================= */

export default function ProgramAccountingPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[120px]">
          <ProgramHero />
          <WhoItsFor />
          <ProgramStructure />
          <Pricing />
          <ProgramFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
